import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Phone, Mail } from "lucide-react";

const STATUS = { UJ: "Új", FELVETTUK: "Felvettük a kapcsolatot", ELSZALLITVA: "Elszállítva", ELUTASITVA: "Elutasítva" };
const STATUS_COLORS = {
  UJ: "bg-[#F3EDE2] text-[#8B4A2E] border-[#C29B78]/50",
  FELVETTUK: "bg-[#EAF0EC] text-[#3B5843] border-[#4A6B53]/30",
  ELSZALLITVA: "bg-[#2C221E] text-[#FBF8F3]",
  ELUTASITVA: "bg-red-50 text-red-700 border-red-200",
};
const WASTE = { konyhai: "Konyhai", kerti: "Kerti", mindketto: "Konyhai + kerti" };
const AMOUNT = { zsak_1: "~1 zsák", zsak_2_5: "2–5 zsák", zsak_5plusz: "5+ zsák", rendszeres: "Rendszeres" };

export default function AdminPickups() {
  const [status, setStatus] = useState("all");
  const [data, setData] = useState({ requests: [], count: 0, new_count: 0 });
  const [loading, setLoading] = useState(true);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/pickup-requests", { params: { status } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [status, bump]);

  const updateStatus = async (id, s) => {
    try {
      await api.patch(`/admin/pickup-requests/${id}`, { status: s });
      toast.success(`Státusz: ${STATUS[s]}`);
      setBump((b) => b + 1);
    } catch { toast.error("Nem sikerült frissíteni"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-pickups">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="section-label">Gyűjtés · elszállítás kérések</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#201B18] mt-1">{data.new_count} új · {data.count} összesen</h1>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-pickups-filter" className="w-full sm:w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Minden státusz</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3" data-testid="admin-pickups-list">
        {loading ? <p className="text-center text-[#8C8075] py-10">Betöltés...</p>
        : data.requests.length === 0 ? <p className="text-center text-[#8C8075] py-10" data-testid="admin-pickups-empty">Még nincs kérés.</p>
        : data.requests.map((r) => (
          <div key={r.id} className="rounded-2xl bg-white border border-[#2C221E]/10 p-4 sm:p-5" data-testid={`admin-pickup-item-${r.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="space-y-1.5 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-[#201B18]">{r.name}</span>
                  <Badge className={STATUS_COLORS[r.status] + " font-mono-brand text-[10px]"}>{STATUS[r.status]}</Badge>
                  <span className="text-xs text-[#8C8075]">{new Date(r.created_at).toLocaleString("hu-HU")}</span>
                </div>
                <p className="text-sm text-[#5C524A] flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {r.address}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#5C524A]">
                  {r.phone && <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 hover:underline"><Phone className="h-3.5 w-3.5" /> {r.phone}</a>}
                  {r.email && <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:underline"><Mail className="h-3.5 w-3.5" /> {r.email}</a>}
                </div>
                <p className="text-xs text-[#8C8075]">{WASTE[r.waste_type]} · {AMOUNT[r.amount]}</p>
                {r.message && <p className="text-sm text-[#201B18] bg-[#FBF8F3] rounded-xl p-3 mt-2 whitespace-pre-wrap">{r.message}</p>}
              </div>
              <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                <SelectTrigger data-testid={`admin-pickup-status-${r.id}`} className="w-full sm:w-56 h-9 text-xs shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
